<template>
  <form
    novalidate
    class="d-flex flex-column justify-content-between needs-validation overflow-x-hidden"
    @submit="submit"
  >
    <BootstrapAlert v-if="fatalError" variant="danger">{{ fatalError }}</BootstrapAlert>
    <div class="flex-grow-1">
      <div class="p-0">
        <div v-if="selectedOption !== 'Category'" class="row">
          <BootstrapSelect
            v-model="productCategoryName"
            :options="productCategoryOptions"
            name="Product Category"
            required
            :error="errorMessages.get('category_id')"
            :server-error="serverError"
          />
        </div>
        <div v-if="selectedOption === 'Group'" class="row">
          <BootstrapSelect
            v-model="productSubCategoryName"
            :options="productSubCategoryOptions"
            name="Product Sub-Category"
            required
            :error="errorMessages.get('subcategory_id') || productSubCategoryOptionsError"
            :server-error="serverError || productSubCategoryOptionsError.length > 0"
          />
        </div>
        <div class="row">
          <BootstrapFormControl
            v-model="name"
            name="Name"
            required
            :error="errorMessages.get('name')"
            :server-error="serverError"
          />
        </div>
        <div class="row mb-4">
          <BootstrapFormControl
            v-model="skuPart"
            name="SKU Code"
            placeholder=""
            :error="errorMessages.get('sku_part')"
            :server-error="serverError"
          />
        </div>
      </div>
    </div>
    <div class="d-flex justify-content-end w-100">
      <BootstrapButton
        name="submit"
        class="me-2"
        type="submit"
        :disabled="!!fatalError || loading"
        :loading="loading"
        @click.stop
      >
        Save
      </BootstrapButton>
      <BootstrapButton name="cancel" variant="secondary" @click="$emit('close')">
        Cancel
      </BootstrapButton>
    </div>
  </form>
</template>
<script lang="ts" setup>
import {Detail} from '@/api/errorMessage';
import {
  ProductCategoryCreateUpdate,
  ProductGroupCreateUpdate,
  ProductSubCategoryCreateUpdate,
} from '@/stores/userManagement/userManagement';

/**
 * Attempting to keep the number of form components to a minimum.
 * TODO: Come back to this form when writing the rest of the page forms.
 * It might be better to rename & organise all the forms differently.
 */

const props = defineProps<{
  cid?: number;
  type?: string;
  isUpdateForm: boolean;
  selectedOption: string;
}>();

const emit = defineEmits<{
  (event: 'submit'): void;
  (event: 'close'): void;
}>();

// Stores
const productCategoriesStore = useProductCategoriesStore();
const productSubCategoriesStore = useProductSubCategoriesStore();
const productGroupsStore = useProductGroupsStore();

// Store refs
const getProductCategories = computed(() => productCategoriesStore.getProductCategories);
const getProductSubCategories = computed(() => productSubCategoriesStore.getProductSubCategories);
const getProductGroups = computed(() => productGroupsStore.getProductGroups);

const loading = ref(false);

// Used to store errors that are never supposed to happen
const fatalError = ref<string | undefined>();
const fatal = (error: string): Error => {
  fatalError.value = error;
  return Error(`Fatal error: ${error}`);
};

// Form Data
const name = ref('');
const skuPart = ref('');
const errorMessages = ref(new Map());
// Used to tell the input not to use client-side validation
const serverError = ref(false);

const sku = ref('');

const productCategoryOptions = computed(() => getProductCategories.value.map(({name}) => name));
const productCategoryName = ref(productCategoryOptions.value[0] || '');
const productCategory = computed(() =>
  getProductCategories.value.find(({name}) => productCategoryName.value === name),
);

// Options must contain Sub-Categories that match the selected Category
const productSubCategoryOptions = computed(() => {
  const options = getProductSubCategories.value.filter(
    (sc) => sc.category_id === productCategory.value?.id,
  );
  return options.map(({name}) => name);
});

// Show an error for the Sub-Category select if there are no options
const productSubCategoryOptionsError = computed(() => {
  return productSubCategoryOptions.value.length === 0
    ? 'The selected Product Category has no Sub-Categories. Please create a Sub-Category first.'
    : '';
});
const productSubCategoryName = ref(productSubCategoryOptions.value[0] || '');

const productSubCategory = computed(() =>
  getProductSubCategories.value.find(({name}) => productSubCategoryName.value === name),
);

// Reset the Sub-Category select when the Category changes
watch(productCategory, () => {
  productSubCategoryName.value = productSubCategoryOptions.value[0];
});

/**
 * Three forms(Category, Sub-Category & Group) in one.
 * Conditionally called store submit functions based on radio button
 * group selection(POST) or based on props.isUpdateForm & props.type(PUT).
 */

// Custom error to avoid 500s in BE
const skuExistsError = [{loc: ['body', 'sku_part'], msg: 'SKU already exists'}];

const createCategorySkuPartExists = computed(() =>
  getProductCategories.value?.find((pc) => pc.sku_part === skuPart.value),
);
const updateCategorySkuPartExists = computed(() =>
  getProductCategories.value
    ?.filter((pc) => pc.sku !== sku.value)
    .find((pc) => pc.sku_part === skuPart.value),
);

const categoryStoreSubmit = async () => {
  const category: ProductCategoryCreateUpdate = {
    name: name.value,
    sku_part: skuPart.value,
  };
  if (props.isUpdateForm === false) {
    if (createCategorySkuPartExists.value) throw skuExistsError;
    await productCategoriesStore.createProductCategory(category);
  } else {
    const updateCategory: ProductCategoryCreateUpdate = {
      ...category,
      id: props.cid,
    };
    if (updateCategorySkuPartExists.value) throw skuExistsError;
    await productCategoriesStore.updateProductCategory(updateCategory);
  }
};

const takenSubCategorySkus = computed(() =>
  getProductSubCategories.value.filter((pc) => {
    const cSkuPart = pc.sku.slice(0, 2); // Category SKUs are two letters
    return cSkuPart === productCategory.value?.sku_part;
  }),
);
const createSubCategorySkuPartExists = computed(() =>
  takenSubCategorySkus.value?.find((pc) => pc.sku_part === skuPart.value),
);
const updateSubCategorySkuPartExists = computed(() =>
  takenSubCategorySkus.value
    ?.filter((pc) => pc.sku !== sku.value)
    .find((pc) => pc.sku_part === skuPart.value),
);

const subCategoryStoreSubmit = async () => {
  const subCategory: ProductSubCategoryCreateUpdate = {
    name: name.value,
    sku_part: skuPart.value,
    category_id: getProductCategories.value?.find((pc) => productCategoryName.value === pc?.name)!
      .id,
  };
  if (props.isUpdateForm === false) {
    if (createSubCategorySkuPartExists.value) throw skuExistsError;
    await productSubCategoriesStore.createProductSubCategory(subCategory);
  } else {
    const updateSubCategory: ProductSubCategoryCreateUpdate = {
      ...subCategory,
      id: props.cid,
    };
    if (updateSubCategorySkuPartExists.value) throw skuExistsError;
    await productSubCategoriesStore.updateProductSubCategory(updateSubCategory);
  }
};

const takenGroupSkus = computed(() =>
  getProductGroups.value.filter((pc) => {
    const cSkuPart = pc.sku.slice(2, 4); // Category SKUs are two letters
    return cSkuPart === productSubCategory.value?.sku_part;
  }),
);
const createGroupSkuPartExists = computed(() =>
  takenGroupSkus.value?.find((pc) => pc.sku_part === skuPart.value),
);
const updateGroupSkuPartExists = computed(() =>
  takenGroupSkus.value
    ?.filter((pc) => pc.sku !== sku.value)
    .find((pc) => pc.sku_part === skuPart.value),
);

const groupStoreSubmit = async () => {
  const group: ProductGroupCreateUpdate = {
    name: name.value,
    sku_part: skuPart.value,
    subcategory_id: getProductSubCategories.value.find(
      (sc) => productSubCategoryName.value === sc.name,
    )!.id,
  };
  if (props.isUpdateForm === false) {
    if (createGroupSkuPartExists.value) throw skuExistsError;
    await productGroupsStore.createProductGroup(group);
  } else {
    const updateGroup: ProductGroupCreateUpdate = {
      ...group,
      id: props.cid,
    };
    if (updateGroupSkuPartExists.value) throw skuExistsError;
    await productGroupsStore.updateProductGroup(updateGroup);
  }
};

const submitForm = async (form: HTMLFormElement) => {
  try {
    loading.value = true;
    // Perform store requests based on the radio button group selection
    switch (props.selectedOption) {
      case 'Category':
        await categoryStoreSubmit();
        break;
      case 'Sub-Category':
        await subCategoryStoreSubmit();
        break;
      case 'Group':
        await groupStoreSubmit();
        break;
    }
    emit('submit');
  } catch (errorMessage: any) {
    serverError.value = true;
    errorMessages.value = new Map();
    // Remove .was-validated for server-side validation response.
    form.classList.remove('was-validated');
    if (typeof errorMessage === 'object') {
      errorMessage.forEach((d: Detail) => errorMessages.value.set(d.loc[1], d.msg));
    }
    form.classList.remove('was-validated');
  } finally {
    loading.value = false;
  }
};

/**
 * Three forms(Category, Sub-Category & Group) in one.
 * Conditionally called load functions based on based on props.isUpdateForm & props.type.
 */

const loadCategoryForm = async () => {
  const category = getProductCategories.value.find((pc) => pc.id === props.cid);
  if (!category) throw fatal('Expected to find category');
  name.value = category.name;
  skuPart.value = category.sku_part;
  sku.value = category.sku;
};

const loadSubCategoryForm = async () => {
  const subCategory = getProductSubCategories.value.find((psc) => psc.id === props.cid);
  if (!subCategory) throw fatal('Expected to find subcategory');
  // Set the form data
  name.value = subCategory.name;
  skuPart.value = subCategory.sku_part;
  sku.value = subCategory.sku;
  productCategoryName.value = getProductCategories.value.find(
    ({id}) => subCategory.category_id === id,
  )!.name;
};

const loadGroupForm = async () => {
  const group = getProductGroups.value.find((pc) => pc.id === props.cid);
  if (!group) throw fatal('Expected to find group');
  const subCategory = getProductSubCategories.value.find(({id}) => group.subcategory_id === id);
  if (!subCategory) throw fatal('Expected to find subcategory');
  const category = getProductCategories.value.find(({id}) => subCategory.category_id === id);
  if (!category) throw fatal('Expected to find category');
  // Set the form data
  name.value = group.name;
  skuPart.value = group.sku_part;
  sku.value = group.sku;
  productSubCategoryName.value = subCategory.name;
  productCategoryName.value = category.name;
};

// https://getbootstrap.com/docs/4.6/components/forms/?#how-it-works
const submit = async (event: Event) => {
  const form = event.target as HTMLFormElement;
  if (form.checkValidity() === false) {
    event.preventDefault();
    event.stopPropagation();
    form.classList.add('was-validated');
  } else {
    event.preventDefault();
    await submitForm(form);
  }
};

onMounted(async () => {
  if (props.isUpdateForm) {
    if (props.type === 'Category') {
      await loadCategoryForm();
    } else if (props.type === 'Sub-Category') {
      await loadSubCategoryForm();
    } else if (props.type === 'Group') {
      await loadGroupForm();
    }
  }
});
</script>
<style lang="scss" scoped>
.row {
  margin-bottom: 1rem;
}
</style>
